import { createElement } from "react";

import type { IOrderUpdate, ISparepartAssembly } from "@voith-webshop/checkout-interfaces";
import { groupBy, map } from "lodash";
import type { SagaIterator, Task } from "redux-saga";
import { all, call, delay, fork, put, select, spawn, takeLatest, takeLeading } from "redux-saga/effects";
import type { ActionType } from "typesafe-actions";
import { getType } from "typesafe-actions";

// types
import * as orderApi from "@/shop/api/shopping/order";
import OrderUpdateErrorNotice from "@/shop/components/Cart/Messages/OrderUpdateErrorNotice";
import PriceRequestFailNotice from "@/shop/components/Cart/Messages/PriceRequestFailNotice";
import QuickBuyFailNotice from "@/shop/components/Cart/Messages/QuickBuyFailNotice";
import { displayWarningMessage } from "@/shop/components/common/Messages";
import * as CartActions from "@/shop/reducers/shopping/cart/actions";
import type { IShoppingCartPosition } from "@/shop/types/shopping/cart";
import type {
  IOrderPlacementResponse,
  IOrderPositionUpdate,
  IOrderPositionUpdatePartial,
  IOrderUpdatePartial,
  IPartialDeliveryList,
  IPriceRequestPosition,
  IPriceRequestStartOptions,
  IPriceRequestStartResponse,
  IPriceRequestTransaction,
  IOrderConfirmationInfo
} from "@/shop/types/shopping/order";

import { getEnabledOrderPositions, moveToOnRequestPositions } from "../cart";
import * as OrderActions from "./actions";
import * as CurrencySelectionActions from "./currencySelection/actions";
import { getPartialDeliveryPositions, getPriceRequest } from "./selectors";
import type { ISparepartAssemblyPositions } from "./sparepartAssembly";
import { getSparepartAssemblyPositions } from "./sparepartAssembly";
import type { OrderAction } from "./types";

// actions

// selectors

// api and logic functions

// const logTable = console && console.table ? console.table : () => null;
const logTable = (_t: unknown): void => null!;

/**
 * Unhandled error in checkout
 * @todo TODO: handle error case separately for each of these cases
 * @param message
 * @param error
 */
// eslint-disable-next-line require-yield
function* unhandledError(message: string, error: Error): SagaIterator<void> {
  console.error(message, error);
}

/**
 * Started by startPriceRequests
 * @param startOptions single request
 */
function* startSinglePriceRequest(startOptions: IPriceRequestStartOptions): SagaIterator<IPriceRequestStartResponse | null> {
  try {
    const request = (yield call(orderApi.startPriceRequest, startOptions)) as IPriceRequestStartResponse;
    return request;
  } catch {
    displayWarningMessage(createElement(PriceRequestFailNotice));
    return null;
  }
}

/**
 * This happens when starting a price request from the cart
 * @param action Redux action
 */
function* startPriceRequests(action: ActionType<typeof OrderActions.startPriceRequests>): SagaIterator {
  const { navigate } = action.payload;
  try {
    void navigate("/cart/order/priceRequest/loader");
    yield put(OrderActions.startPriceRequestsStart());

    const orderPositions = (yield select(getEnabledOrderPositions)) as IShoppingCartPosition[];

    logTable(orderPositions);

    // clear existing partial deliveries
    yield call(orderApi.clearPartialDeliveries);

    const requestOptions = (yield call(orderApi.getOrderCommissions)) as IPriceRequestStartOptions[];
    logTable(requestOptions);

    // start all requests in parallel
    const responses = (yield all(
      requestOptions.map((request) => {
        return call(startSinglePriceRequest, request);
      })
    )) as IPriceRequestStartResponse[];

    // in case of error, they return null
    const validResponses = responses.filter(Boolean);
    logTable(validResponses);

    // indicate success
    yield put(OrderActions.startPriceRequestsSuccess());

    if (validResponses.length > 1) {
      // navigate to commissions page
      void navigate("/cart/commissions", { replace: true });
    } else if (validResponses.length === 1) {
      // navigate directly to the price request page
      const priceRequestId = validResponses[0]!.id;
      void navigate(`/cart/order/priceRequest/${encodeURIComponent(priceRequestId)}`, { replace: true });
    } else {
      // error already reported previously
      void navigate(-1);
    }
  } catch (err) {
    // indicate failure
    displayWarningMessage(createElement(PriceRequestFailNotice));
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderActions.startPriceRequestsFail(err));
    // navigate back from the loader page so the user doesn't get stuck
    void navigate(-1);
  }
}

/**
 * This happens when starting quick buy from the quote history or dashboard
 * @param action Redux action
 */
function* startOfferToOrder(action: ActionType<typeof OrderActions.startOfferToOrder>): SagaIterator {
  const { navigate } = action.payload;
  try {
    void navigate("/cart/order/priceRequest/loader");
    yield put(OrderActions.startPriceRequestsStart());

    yield call(orderApi.clearPartialDeliveries);

    const priceRequest = (yield call(orderApi.startOfferToOrder, action.payload)) as IPriceRequestTransaction;

    yield put(OrderActions.startPriceRequestsSuccess());

    // navigate to order page
    void navigate(`/cart/order/priceRequest/${encodeURIComponent(priceRequest.id)}`, { replace: true });
  } catch (err) {
    displayWarningMessage(createElement(QuickBuyFailNotice));
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderActions.startPriceRequestsFail(err));
    // navigate back from the loader page so the user doesn't get stuck
    void navigate(-1);
  }
}

/**
 * This happens when starting partial quick buy from the quote history
 * @param action Redux action
 */
function* startPartialOfferToOrder(action: ActionType<typeof OrderActions.startPartialOfferToOrder>): SagaIterator {
  const { navigate, offerId, itemNumbers } = action.payload;
  try {
    void navigate("/cart/order/priceRequest/loader");
    yield put(OrderActions.startPriceRequestsStart());

    yield call(orderApi.clearPartialDeliveries);

    const priceRequest = (yield call(orderApi.starPartialOfferToOrder, {
      offerId,
      itemNumbers
    })) as IPriceRequestTransaction;

    yield put(OrderActions.startPriceRequestsSuccess());

    // navigate to order page
    void navigate(`/cart/order/priceRequest/${encodeURIComponent(priceRequest.id)}`, { replace: true });
  } catch (err) {
    displayWarningMessage(createElement(QuickBuyFailNotice));
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderActions.startPriceRequestsFail(err));
    // navigate back from the loader page so the user doesn't get stuck
    void navigate(-1);
  }
}

/**
 * This happens when starting a partial delivery from checkout step 1
 * @param action
 */
function* splitPriceRequest(action: ActionType<typeof OrderActions.splitPriceRequest>): SagaIterator {
  try {
    const priceRequest = (yield select(getPriceRequest)) as IPriceRequestTransaction;
    const { navigate } = action.payload;

    const orderPositions = (yield select(getPartialDeliveryPositions)) as IPriceRequestPosition[];
    const orderPositionsByDate = groupBy(orderPositions, "partialDeliveryNo");

    if (Object.keys(orderPositionsByDate).length < 2) {
      throw new Error("Error splitting cart");
    }

    // delete the existing price request
    yield call(orderApi.clearPartialDelivery, priceRequest.id);

    // create new price requests from the nex positions
    const responses = (yield all(
      map(orderPositionsByDate, (partialDeliveryPositions, partialDeliveryNo: string) => {
        const requestedDeliveryDate = partialDeliveryPositions[0]!.requestedDeliveryDate;
        const request = {
          productType: priceRequest.transactionProductType,
          sysId: priceRequest.sysId,
          org: priceRequest.org,
          parentId: priceRequest.id,
          requestedDeliveryDate,
          positions: partialDeliveryPositions,
          partialDeliveryNo,
          currency: priceRequest.currency
        };
        return call(orderApi.startPriceRequest, request);
      })
    )) as IPriceRequestStartResponse;
    logTable(responses);

    // navigate to commissions page
    void navigate("/cart/commissions");
  } catch (err) {
    displayWarningMessage(createElement(PriceRequestFailNotice));
    console.error(err);
  }
}

function* fetchCommissions(_: ActionType<typeof OrderActions.fetchCommissions>): SagaIterator {
  try {
    const commissionsResponse = (yield call(orderApi.getPartialDeliveryList)) as IPartialDeliveryList;
    // @ts-expect-error - type clash
    yield put(OrderActions.fetchCommissionsSuccess(commissionsResponse.transactions));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderActions.fetchCommissionsFail(err));
  }
}

function* removeCommission(action: ActionType<typeof OrderActions.removeCommission>): SagaIterator {
  try {
    yield call(orderApi.clearPartialDelivery, action.payload);
    yield put(OrderActions.fetchCommissions());
  } catch (err) {
    // TODO: figure out proper error handling here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(unhandledError, "removeCommission failed", err);
  }
}

//
/**
 * Handles the polling the price request
 * @param action
 * @param isPartial extra logic can be run for partial delivery
 */
function* fetchPriceRequest(action: ActionType<typeof OrderActions.fetchPriceRequest | typeof OrderActions.fetchPartialPriceRequest>): SagaIterator<void> {
  try {
    const priceRequestId: string = action.payload;

    // flag as fetching
    yield put(OrderActions.fetchPriceRequestStart(priceRequestId));

    // initial call to get estimated poll duration
    let priceRequestResponse = (yield call(orderApi.fetchPriceRequest, priceRequestId)) as IPriceRequestTransaction;

    if (priceRequestResponse.estimatedPollDuration! > 0) {
      priceRequestResponse = (yield call(waitForPriceRequestResponse, priceRequestResponse)) as IPriceRequestTransaction;
    }

    // if there is no success after the loop, the number of attempts is exceeded
    if (!priceRequestResponse.success && !priceRequestResponse.error) {
      throw new Error("Number of retries exceeded");
    }

    if (priceRequestResponse.error) {
      // throwing an error here prevents detection of failed quick buy
      // some downstream code might not be able to handle this case
      // throw new Error(priceRequestResponse.error.message);
    }

    // set currencies for currency modal
    if (priceRequestResponse.availableCurrencies) {
      yield put(
        CurrencySelectionActions.setCurrencySelectionModalData({
          currentCurrency: priceRequestResponse.currency!,
          availableCurrencies: priceRequestResponse.availableCurrencies
        })
      );
    }

    // put price request data in store
    yield put(OrderActions.fetchPriceRequestSuccess(priceRequestResponse));

    // move missing items
    yield fork(movePositionsToRequestOnly);
  } catch (err) {
    console.error(err); // do not swallow errors here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderActions.fetchPriceRequestFail(err));
  }
}

/**
 * Called if the price request was not ready initially and needs repeated polling
 */
function* waitForPriceRequestResponse(priceRequest: IPriceRequestTransaction): SagaIterator<IPriceRequestTransaction> {
  // trigger update of progress
  const progressTask = (yield spawn(updateProgress, priceRequest.estimatedPollDuration!)) as Task;

  // total wait time is MAX_ATTEMPTS * DELAY_TIME_MS
  const MAX_ATTEMPTS = 6 * 60;
  const DELAY_TIME_MS = 1000;

  const initialHash = window.location.hash;

  // poll the response (6 minutes)
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    // wait one second
    yield delay(DELAY_TIME_MS);

    if (window.location.hash !== initialHash) {
      // stop polling when the user navigated away
      break;
    }

    // poll again
    priceRequest = (yield call(orderApi.fetchPriceRequest, priceRequest.id)) as IPriceRequestTransaction;

    // stop when successful or error
    if (priceRequest.success || priceRequest.error) {
      break;
    }
  }

  // cancel progress update
  if (progressTask) {
    progressTask.cancel();
  }

  return priceRequest;
}

function* movePositionsToRequestOnly(): SagaIterator {
  const priceRequest = (yield select(getPriceRequest)) as IPriceRequestTransaction;
  const missingPositions = priceRequest.missingPositions;
  if (missingPositions.length > 0) {
    const missingIds = missingPositions.map((pos) => pos.id);
    yield put(moveToOnRequestPositions(missingIds));
  }
}

function* updateProgress(estimatedPollDuration: number, progressTime = 0): Generator {
  const step = Math.max(250, estimatedPollDuration / 10);
  const progress = Math.min(100, Math.round((100 * progressTime) / estimatedPollDuration));

  yield put(OrderActions.fetchPriceRequestProgress(progress));

  yield delay(step);

  if (progressTime < estimatedPollDuration && progress < 100) {
    yield updateProgress(estimatedPollDuration, progressTime + step);
  }
}

function* fetchOrderDetails(action: ActionType<typeof OrderActions.fetchOrderDetails>): SagaIterator {
  try {
    const priceRequestId: string = action.payload;
    const priceRequestResponse = (yield call(orderApi.fetchOrderDetails, priceRequestId)) as IPriceRequestTransaction;
    yield put(OrderActions.fetchPriceRequestSuccess(priceRequestResponse));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderActions.fetchPriceRequestFail(err));
  }
}

function* fetchOrderForModify(action: ActionType<typeof OrderActions.fetchOrderForModify>): SagaIterator {
  try {
    const priceRequestId: string = action.payload;
    const priceRequestResponse = (yield call(orderApi.fetchOrderForModify, priceRequestId)) as IPriceRequestTransaction;
    yield put(OrderActions.fetchPriceRequestSuccess(priceRequestResponse));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderActions.fetchPriceRequestFail(err));
  }
}

function* fetchOrderForCheckout(action: ActionType<typeof OrderActions.fetchOrderForCheckout>): SagaIterator {
  try {
    const priceRequestId: string = action.payload;
    const priceRequestResponse = (yield call(orderApi.fetchOrderForCheckout, priceRequestId)) as IPriceRequestTransaction;

    yield put(OrderActions.fetchPriceRequestSuccess(priceRequestResponse));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderActions.fetchPriceRequestFail(err));
  }
}

// TODO: check if using this pattern is a really good idea
// only of those should run at the same time, since they use the same reducer
function* priceRequestFetchSaga(action: OrderAction): SagaIterator {
  switch (action.type) {
    case getType(OrderActions.fetchPriceRequest):
      yield call(fetchPriceRequest, action);
      break;
    case getType(OrderActions.fetchPartialPriceRequest):
      yield call(fetchPriceRequest, action);
      break;
    case getType(OrderActions.fetchOrderDetails):
      yield call(fetchOrderDetails, action);
      break;
    case getType(OrderActions.fetchOrderForModify):
      yield call(fetchOrderForModify, action);
      break;
    case getType(OrderActions.fetchOrderForCheckout):
      yield call(fetchOrderForCheckout, action);
      break;
    default:
      break;
  }
}

/**
 * This is started by pressing the "Update prices" button in step 1 of the checkout
 * @param action redux action
 */
function* modifyPriceRequest(action: ActionType<typeof OrderActions.modifyPriceRequest>): SagaIterator {
  const { navigate } = action.payload;
  try {
    const priceRequest = (yield select(getPriceRequest)) as IPriceRequestTransaction;

    const newPriceRequest: IPriceRequestStartOptions = {
      productType: priceRequest.transactionProductType,
      sysId: priceRequest.sysId,
      org: priceRequest.org,
      taxCategory: priceRequest?.taxCategory,
      previousId: priceRequest.id,
      parentId: priceRequest.parentId,
      positions: priceRequest.orderPositions,
      requestedDeliveryDate: priceRequest.requestedDeliveryDate,
      partialDeliveryNo: priceRequest.partialDeliveryNo,
      currency: priceRequest.currency
    };

    const response = (yield call(orderApi.startPriceRequest, newPriceRequest)) as IPriceRequestStartResponse;
    yield put(OrderActions.setWaitingForUpdate(false));

    void navigate(`/cart/order/priceRequest/redirect/${encodeURIComponent(response.id)}`);
  } catch (err) {
    // TODO: figure out proper error handling here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(unhandledError, "modifyPriceRequest failed", err);
  }
}

/**
 * This is started by selecting another currency in checkout step 1
 * @param action redux action
 */
function* modifyPriceRequestFromCurrencyModal(action: ActionType<typeof OrderActions.modifyPriceRequestFromCurrencyModal>): SagaIterator {
  try {
    const { selectedCurrency, navigate } = action.payload;
    const priceRequest = (yield select(getPriceRequest)) as IPriceRequestTransaction;
    const newPriceRequest: IPriceRequestStartOptions = {
      productType: priceRequest.transactionProductType,
      sysId: priceRequest.sysId,
      org: priceRequest.org,
      taxCategory: priceRequest?.taxCategory,
      previousId: priceRequest.id,
      parentId: priceRequest.parentId,
      positions: priceRequest.orderPositions,
      requestedDeliveryDate: priceRequest.requestedDeliveryDate,
      partialDeliveryNo: priceRequest.partialDeliveryNo,
      currency: selectedCurrency
    };

    const response = (yield call(orderApi.startPriceRequest, newPriceRequest)) as IPriceRequestStartResponse;
    void navigate(`/cart/order/priceRequest/redirect/${response.id}`);
    /** in case of priceRequest update through modal this needs to be called in order to trigger an update of the PriceRequestContainer */
    yield put(CurrencySelectionActions.updateCurrencySelectionModal({ selectedCurrency })); // update render key
  } catch (err) {
    // TODO: figure out proper error handling here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(unhandledError, "modifyPriceRequestFromCurrencyModal failed", err);
  }
}

/**
 * Update order while preserving existing properties
 * @private
 * @param action
 */
function* updateOrderInternal(action: ActionType<typeof OrderActions.updateOrder>): SagaIterator {
  try {
    const partialUpdate: IOrderUpdatePartial = action.payload;
    const transaction = (yield select(getPriceRequest)) as IPriceRequestTransaction;
    const update = buildUpdateOrderPatch(transaction, partialUpdate);
    yield put(OrderActions.updateOrderStart());
    const priceRequestResponse = (yield call(orderApi.updateOrder, update.id, update)) as IPriceRequestTransaction;
    yield put(OrderActions.updateOrderSuccess(priceRequestResponse, action.meta));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderActions.updateOrderFail(err, action.meta));
    console.error("updateOrderInternal failed", err);
  }
}

function buildUpdateOrderPatch(transaction: IPriceRequestTransaction, partialUpdate: IOrderUpdatePartial): IOrderUpdate {
  // compose order position update
  const orderPositionUpdate: IOrderPositionUpdate[] = transaction.orderPositions.map((orderPosition, posIndex) => {
    const posPartial: Partial<IOrderPositionUpdate> = partialUpdate?.orderPositions?.[posIndex] ?? {};

    return {
      newCustomerMaterialNumber: posPartial.newCustomerMaterialNumber! ?? orderPosition.newCustomerMaterialNumber!,
      comment: posPartial.comment! ?? orderPosition.comment!,
      deliveries: posPartial.deliveries ?? orderPosition.deliveries,
      // special treatment, so we can remove the spare part assembly property
      sparePartAssembly: "sparePartAssembly" in posPartial ? posPartial.sparePartAssembly! : orderPosition.sparePartAssembly!
    };
  });

  // compose order update and preserve existing properties
  const orderUpdate: IOrderUpdate = {
    id: transaction.id,
    orderPositions: orderPositionUpdate,
    // preserve existing data
    requestedDeliveryDate: partialUpdate.requestedDeliveryDate ?? transaction.requestedDeliveryDate!,
    isCompleteDelivery: partialUpdate.isCompleteDelivery ?? transaction.isCompleteDelivery,
    goodsRecipientNo: partialUpdate.goodsRecipientNo ?? transaction.goodsRecipientNo,
    refNo: partialUpdate.refNo ?? transaction.refNo!,
    comment: partialUpdate.comment ?? transaction.comment!,
    requiredMarking: partialUpdate.requiredMarking ?? transaction.requiredMarking!,
    equipmentNo: partialUpdate.equipmentNo ?? transaction.equipmentNo!,
    requestedFastLaneDelivery: partialUpdate.requestedFastLaneDelivery ?? transaction.requestedFastLaneDelivery!,
    requestedAccessories: partialUpdate.requestedAccessories ?? transaction.requestedAccessories,
    // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    deliveryWeightClassificationId: partialUpdate.deliveryWeightClassificationId ?? transaction.deliveryWeightClassification?.id!,
    // note: currency can only be update on new price request
    invoiceRecipientNo: partialUpdate.invoiceRecipientNo ?? transaction.invoiceRecipientNo,
    shutDownDate: partialUpdate.shutDownDate ?? transaction.shutDownDate!
  };

  return orderUpdate;
}

function* proceedToOrderEdit(action: ActionType<typeof OrderActions.proceedToOrderEdit>): SagaIterator {
  try {
    const { id, navigate } = action.payload;
    // reset currency modal input data
    yield put(CurrencySelectionActions.clearCurrencySelectionData());
    // navigate to order edit page
    void navigate(`/cart/order/edit/${encodeURIComponent(id)}`);
  } catch (err) {
    // TODO: figure out proper error handling here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(unhandledError, "proceedToOrderEdit failed", err);
  }
}

function* updateDeliveryDatesForCompleteDelivery(): SagaIterator {
  try {
    {
      const transaction = (yield select(getPriceRequest)) as IPriceRequestTransaction;

      // compose order position update
      const orderPositionUpdate: IOrderPositionUpdatePartial[] = transaction.orderPositions.map((orderPosition) => {
        const deliveries = orderPosition.deliveries.map((delivery) => ({
          ...delivery,
          requestedDeliveryDate: transaction.requestedDeliveryDate! // set the delivery date to the latest date
        }));

        return {
          deliveries
        };
      });

      // compose order update and preserve existing properties
      const orderUpdate: IOrderUpdatePartial = {
        id: transaction.id,
        isCompleteDelivery: true,
        orderPositions: orderPositionUpdate
      };

      // update order transaction (dot not put the effect, else async/await effect does not work)
      yield call(updateOrderInternal, OrderActions.updateOrder(orderUpdate));
    }
  } catch (err) {
    console.error("updateDeliveryDatesForCompleteDelivery failed", err);
  }
}

function* updateAssemblyInfo(): SagaIterator {
  try {
    {
      // update assembly info: date and comment
      const assemblyInfo = (yield select(getSparepartAssemblyPositions)) as ISparepartAssemblyPositions;
      const transaction = (yield select(getPriceRequest)) as IPriceRequestTransaction;

      // compose order position update
      const orderPositionUpdate: IOrderPositionUpdatePartial[] = transaction.orderPositions.map((orderPosition) => {
        // this might be null
        const currentAssemblyInfo = assemblyInfo?.[orderPosition.id];

        // copy info from redux state for assembly, if enabled
        const sparePartAssembly: ISparepartAssembly = currentAssemblyInfo?.isEnabled
          ? {
              comment: currentAssemblyInfo.comment,
              requestedAssemblyDate: currentAssemblyInfo.requestedDate
            }
          : undefined!; // the validation framework in the server will throw error if null

        return {
          sparePartAssembly
        };
      });

      // compose order update
      const orderUpdate: IOrderUpdatePartial = {
        id: transaction.id,
        orderPositions: orderPositionUpdate
      };

      // update order transaction (dot not put the effect, else async/await effect does not work)
      yield call(updateOrderInternal, OrderActions.updateOrder(orderUpdate));

      // FIXME: this needs to be done somewhere
      // yield put(SparePartAssemblyActions.clearSparepartAssemblyData());
    }
  } catch (err) {
    displayWarningMessage(createElement(OrderUpdateErrorNotice));
    // TODO: figure out proper error handling here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(unhandledError, "updateAssemblyInfo failed", err);
  }
}

function* proceedToOrderCheckout(action: ActionType<typeof OrderActions.proceedToOrderCheckout>): SagaIterator {
  try {
    const { updatePartial, navigate } = action.payload;

    const { id } = (yield select(getPriceRequest)) as IPriceRequestTransaction;
    yield call(updateOrderInternal, OrderActions.updateOrder(updatePartial));

    // go to order review page
    void navigate(`/cart/order/review/${encodeURIComponent(id)}`);
  } catch (err) {
    displayWarningMessage(createElement(OrderUpdateErrorNotice));
    // TODO: figure out proper error handling here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(unhandledError, "proceedToOrderCheckout failed", err);
  }
}

function* proceedFromOrderEdit(action: ActionType<typeof OrderActions.proceedFromOrderEdit>): SagaIterator {
  try {
    const { updatePartial, navigate } = action.payload;
    // save data from order edit form
    yield call(updateOrderInternal, OrderActions.updateOrder(updatePartial));

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const priceRequest: IPriceRequestTransaction = yield select(getPriceRequest);
    const { id } = priceRequest;
    return navigate(`/cart/order/delivery-conditions/${encodeURIComponent(id)}`);
  } catch (err) {
    displayWarningMessage(createElement(OrderUpdateErrorNotice));
    // TODO: figure out proper error handling here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(unhandledError, "proceedFromOrderEdit failed", err);
  }
}

function* proceedWithCompleteDelivery(action: ActionType<typeof OrderActions.proceedWithCompleteDelivery>): SagaIterator {
  try {
    // update delivery dates
    yield call(updateDeliveryDatesForCompleteDelivery);
    // update assembly info
    yield call(updateAssemblyInfo);
    // go to order edit page
    yield put(OrderActions.proceedToOrderEdit(action.payload));
  } catch (err) {
    displayWarningMessage(createElement(OrderUpdateErrorNotice));
    // TODO: figure out proper error handling here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(unhandledError, "proceedWithCompleteDelivery failed", err);
  }
}

function* proceedWithPartialDelivery(action: ActionType<typeof OrderActions.proceedWithPartialDelivery>): SagaIterator {
  try {
    const { navigate } = action.payload;
    void navigate(`/cart/order/edit/${encodeURIComponent(action.payload.id)}`);
  } catch (err) {
    // TODO: figure out proper error handling here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield call(unhandledError, "proceedWithPartialDelivery failed", err);
  }
}

/**
 * Called when pressing order in checkout step 3
 * @param action
 */

function* confirmOrder(action: ActionType<typeof OrderActions.confirmOrder>): SagaIterator {
  const { navigate, order, sysId } = action.payload;

  try {
    yield put(OrderActions.confirmOrderStart());

    const res = (yield call(orderApi.confirmOrder, order.id, order)) as IOrderPlacementResponse;
    // TODO: consider handling this differently in the server, because there are multiple reasons for failure
    if (!res.success) {
      throw new Error("Confirmation failed");
    }
    yield put(OrderActions.confirmOrderSuccess(res, action.meta));
    void navigate("/cart/order/confirmation", { state: { orderNo: encodeURIComponent(res.orderNo) } });
  } catch (err) {
    // It's a hack and needs to be removed as soon as the main problem is solved
    if (sysId === "NAVVT") {
      const fakeResponse = {
        success: true,
        fail: false,
        orderNo: undefined! as string
      };
      yield put(OrderActions.confirmOrderSuccess(fakeResponse, action.meta));
      void navigate("/cart/order/confirmation");
    } else {
      void navigate(`/cart/order/confirmation_failed`);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      yield put(OrderActions.confirmOrderFail(err, action.meta));
    }
  }
}

/**
 * Called when entering order in checkout step 4
 * @param action
 */
function* fetchOrderConfirmation(action: ActionType<typeof OrderActions.fetchOrderConfirmation>): SagaIterator {
  try {
    yield put(OrderActions.fetchOrderConfirmationStart());
    const confirmationInfo = (yield call(orderApi.fetchOrderConfirmation, action.payload)) as IOrderConfirmationInfo;
    yield put(OrderActions.fetchOrderConfirmationSuccess(confirmationInfo));
    yield put(CartActions.checkSync());
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    yield put(OrderActions.fetchOrderConfirmationFail(err));
  }
}

// TODO: consider usage of takeLeading takeLatest
function* priceRequestSaga(): SagaIterator {
  // handle price request start
  yield takeLeading(getType(OrderActions.startPriceRequests), startPriceRequests);
  yield takeLeading(getType(OrderActions.startOfferToOrder), startOfferToOrder);
  yield takeLeading(getType(OrderActions.startPartialOfferToOrder), startPartialOfferToOrder);

  // handle fetching, only one of these runs at the same time
  yield takeLeading(
    [
      getType(OrderActions.fetchPriceRequest),
      getType(OrderActions.fetchPartialPriceRequest),
      getType(OrderActions.fetchOrderDetails),
      getType(OrderActions.fetchOrderForModify),
      getType(OrderActions.fetchOrderForCheckout)
    ],
    priceRequestFetchSaga
  );

  // updating order
  yield takeLatest(getType(OrderActions.updateOrder), updateOrderInternal);
  yield takeLatest(getType(OrderActions.proceedToOrderCheckout), proceedToOrderCheckout);
  yield takeLatest(getType(OrderActions.proceedFromOrderEdit), proceedFromOrderEdit);

  // to step 2
  yield takeLatest(getType(OrderActions.proceedToOrderEdit), proceedToOrderEdit);
  yield takeLatest(getType(OrderActions.proceedWithCompleteDelivery), proceedWithCompleteDelivery);
  yield takeLatest(getType(OrderActions.proceedWithPartialDelivery), proceedWithPartialDelivery);

  yield takeLatest(getType(OrderActions.confirmOrder), confirmOrder);

  // confirmation
  yield takeLatest(getType(OrderActions.fetchOrderConfirmation), fetchOrderConfirmation);

  // modify
  yield takeLeading(getType(OrderActions.modifyPriceRequest), modifyPriceRequest);

  // modify from currency modal, also sets modifies step 1
  yield takeLeading(getType(OrderActions.modifyPriceRequestFromCurrencyModal), modifyPriceRequestFromCurrencyModal);

  // split into partial deliveries
  yield takeLeading(getType(OrderActions.splitPriceRequest), splitPriceRequest);
}

function* commissionsSaga(): SagaIterator {
  yield takeLatest(getType(OrderActions.fetchCommissions), fetchCommissions);
  yield takeLatest(getType(OrderActions.removeCommission), removeCommission);
}

export default function* orderSaga(): SagaIterator {
  yield fork(priceRequestSaga);
  yield fork(commissionsSaga);
}
